import type { createOwnerUserBody } from "@nlc/shared-types";
import type React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { ownerSchema } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import {
  FieldError,
  Input,
  Label,
  LabeledGroup,
  TextField,
} from "@/components/field";
import { Switch, SwitchField } from "@/components/switch";
import { Text } from "@/components/text";

import { useCreateOwner } from "../hooks/useCreateUser";
import { useCreateUserResolveProducts } from "../hooks/useCreateUserResolveProducts";
import { BasicInformation } from "./BasicInformation";
import { CreatingUserOverlay } from "./CreatingUserOverlay";

export default function CreateOwner() {
  const navigate = useNavigate();
  const { isPending, mutate } = useCreateOwner();
  const { data: products, isLoading: loadingProducts } =
    useCreateUserResolveProducts();

  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
  } = useForm<createOwnerUserBody>({
    defaultValues: {
      company: "",
      email: "",
      isConcierge: false,
      isFreeAccount: false,
      isInternalAccount: false,
      isSunsetShift: false,
      name: "",
      productId: 0,
    },
    mode: "onChange",
    resolver: zodResolver(ownerSchema),
  });

  const onSubmit = (data: createOwnerUserBody) => {
    mutate(data, {
      onSuccess: () => {
        reset();
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CreatingUserOverlay isPending={isPending}>
        <BasicInformation
          control={control}
          emailField="email"
          nameField="name"
        />

        <div className="flex flex-col gap-y-lg mt-3 lg:grid lg:grid-cols-2 lg:gap-x-3xl">
          <Controller
            control={control}
            name="company"
            render={({
              field: { name, onBlur, onChange, ref, value },
              fieldState: { error, invalid },
            }) => (
              <TextField
                isInvalid={invalid}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                validationBehavior="aria"
                value={value}
              >
                <Label requiredHint>Company</Label>
                <Input placeholder="Company name" ref={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />

          <Controller
            control={control}
            name="productId"
            render={({ field, fieldState }) => (
              <LabeledGroup>
                <Label requiredHint>Product</Label>
                <ComboBox
                  allowsEmptyCollection
                  menuTrigger="input"
                  onSelectionChange={(key) =>
                    key && field.onChange(Number(key))
                  }
                  selectedKey={field.value || null}
                >
                  <ComboBoxGroup>
                    <ComboBoxInput
                      placeholder={
                        loadingProducts ? "Loading..." : "Select a product"
                      }
                    />
                    <ComboBoxButton />
                  </ComboBoxGroup>
                  <ComboBoxPopover>
                    <ComboBoxListBox
                      renderEmptyState={() => (
                        <Text className="p-2">No results found</Text>
                      )}
                    >
                      {products?.map((p) => (
                        <ComboBoxListItem id={p.id} key={p.id}>
                          {p.title}
                        </ComboBoxListItem>
                      ))}
                    </ComboBoxListBox>
                  </ComboBoxPopover>
                </ComboBox>
                {fieldState.error && (
                  <FieldError>{fieldState.error.message}</FieldError>
                )}
              </LabeledGroup>
            )}
          />
        </div>

        <div className="mt-5">
          <Toggle
            control={
              <Controller
                control={control}
                name="isFreeAccount"
                render={({ field }) => (
                  <SwitchField className="flex">
                    <Switch
                      isSelected={field.value}
                      onChange={field.onChange}
                    />
                  </SwitchField>
                )}
              />
            }
            description="Non-paying account with complete platform access"
            title="Free account"
          />
          <Toggle
            control={
              <Controller
                control={control}
                name="isInternalAccount"
                render={({ field }) => (
                  <SwitchField>
                    <Switch
                      isSelected={field.value}
                      onChange={field.onChange}
                    />
                  </SwitchField>
                )}
              />
            }
            description="For internal testing only, not counted as active"
            title="Internal account"
          />
          <Toggle
            control={
              <Controller
                control={control}
                name="isSunsetShift"
                render={({ field }) => (
                  <SwitchField>
                    <Switch
                      isSelected={field.value}
                      onChange={field.onChange}
                    />
                  </SwitchField>
                )}
              />
            }
            description="Indicate this account is ste on the sunset shift"
            title="Sunset shift"
          />
          <Toggle
            control={
              <Controller
                control={control}
                name="isConcierge"
                render={({ field }) => (
                  <SwitchField>
                    <Switch
                      isSelected={field.value}
                      onChange={field.onChange}
                    />
                  </SwitchField>
                )}
              />
            }
            description="This account will offer our concierge services"
            title="Concierge"
          />
        </div>
        <div className="flex justify-end mt-5 pt-xl border-t border-border-secondary">
          <Button
            className="mr-2"
            onPress={() => navigate({ to: "/users" })}
            type="button"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            isDisabled={!isValid || isPending}
            type="submit"
            variant="primary"
          >
            Create user
          </Button>
        </div>
      </CreatingUserOverlay>
    </form>
  );
}

interface ToggleProps {
  control: React.ReactNode;
  description: string;
  title: string;
}

function Toggle({ control, description, title }: ToggleProps) {
  return (
    <div className="border border-button-secondary-border py-3 px-4 rounded-sm my-4 flex justify-between items-center gap-lg">
      <div className="min-w-0">
        <p className="text-text-primary font-semibold text-base">{title}</p>
        <p className="text-text-secondary text-sm">{description}</p>
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}
